import { useState } from "react";
import { Globe, Moon, Settings, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { NativeOverlaySheet } from "@/components/native/NativeOverlaySheet";
import { NativeOptionSheet, type NativeOption } from "@/components/native/NativeOptionSheet";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import {
  nativeAppLabels,
  commonMenuLabels,
  languageNamesLabels,
} from "@/i18n/labels/commonLabels";
import { hapticImpact } from "@/lib/native/capacitorBridge";
import { useNativeFloatingBubble } from "@/hooks/useNativeFloatingBubble";
import type { Language } from "@/utils/languageUtils";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "bilenia-native-guest-settings-pos";

/** Logged-out native browse: draggable settings bubble (lang + theme). */
const NativeGuestFloatingSettings = () => {
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const labels = nativeAppLabels(language);
  const menuLabels = commonMenuLabels(language);
  const langNames = languageNamesLabels(language);
  const [open, setOpen] = useState(false);
  const [langSheetOpen, setLangSheetOpen] = useState(false);
  const isDark = theme === "dark";
  const { style, suppressClickRef, pointerHandlers } =
    useNativeFloatingBubble(STORAGE_KEY);

  const languageOptions: NativeOption[] = (["sv", "en"] as const).map(
    (lang) => ({
      value: lang,
      label: langNames[lang],
    }),
  );

  const openSheet = () => {
    if (suppressClickRef.current) return;
    void hapticImpact("light");
    setOpen(true);
  };

  return (
    <>
      <div
        className="native-guest-floating-settings pointer-events-auto fixed z-[85] touch-none"
        style={style}
        {...pointerHandlers}
      >
        <Button
          type="button"
          variant="ghost"
          size="headerIcon"
          className={cn(
            "native-guest-floating-settings-btn native-floating-profile-btn",
            "text-foreground hover:bg-muted/80 bg-background/95 backdrop-blur-md",
            "border border-border shadow-md rounded-full",
          )}
          onClick={openSheet}
          aria-label={labels.settings ?? menuLabels.settings ?? "Inställningar"}
        >
          <Settings className="h-7 w-7" strokeWidth={2.25} />
        </Button>
      </div>

      <NativeOverlaySheet
        open={open}
        onOpenChange={setOpen}
        title={menuLabels.menu ?? menuLabels.settings ?? "Meny"}
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-3 rounded-lg px-4 py-3.5">
            <span className="flex items-center gap-3 text-base">
              {isDark ? (
                <Moon className="h-5 w-5 shrink-0" />
              ) : (
                <Sun className="h-5 w-5 shrink-0" />
              )}
              {isDark ? menuLabels.darkModeActive : menuLabels.lightModeActive}
            </span>
            <Switch
              checked={isDark}
              onCheckedChange={() => {
                void hapticImpact("light");
                toggleTheme();
              }}
              className="shrink-0 native-user-actions-theme-switch"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              window.setTimeout(() => setLangSheetOpen(true), 50);
            }}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3.5 text-left text-base hover:bg-muted active:bg-muted"
          >
            <Globe className="h-5 w-5 shrink-0" />
            {langNames[language]}
          </button>
        </div>
      </NativeOverlaySheet>

      <NativeOptionSheet
        open={langSheetOpen}
        onOpenChange={setLangSheetOpen}
        title={langNames[language]}
        value={language}
        options={languageOptions}
        onSelect={(lang) => {
          setLanguage(lang as Language);
          setLangSheetOpen(false);
        }}
      />
    </>
  );
};

export default NativeGuestFloatingSettings;
